import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  StarIcon, 
  FunnelIcon, 
  XMarkIcon,
  AdjustmentsHorizontalIcon,
  ShoppingBagIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon, HeartIcon as HeartOutlineIcon } from '@heroicons/react/24/solid';
import ProductCard from '../../components/ProductCard';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import { getApiUrl } from '../../config/api';
import { defaultCartSize } from '../../utils/productSize';

const Products = () => {
  const [searchParams] = useSearchParams();
  const category = searchParams.get('category');
  const tag = searchParams.get('tag');
  const { addToCart } = useCart();
  const [showAddedToCart, setShowAddedToCart] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(category || 'all');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [priceRange, setPriceRange] = useState([0, 5000]);
  const [sortBy, setSortBy] = useState('featured');
  const { isWishlisted, toggleWishlist } = useWishlist();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Scroll to top and update selectedCategory when URL category changes
  useEffect(() => {
    // Scroll to top when component mounts or category changes
    window.scrollTo(0, 0);
    
    if (category) {
      setSelectedCategory(category.toLowerCase());
    }
  }, [category]);

  // Remove redirect to /rental-products
  // useEffect(() => {
  //   if (selectedCategory === 'rental') {
  //     navigate('/rental-products');
  //   }
  // }, [selectedCategory, navigate]);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(getApiUrl('/api/admin/categories'));
        if (!response.ok) throw new Error('Failed to fetch categories');
        const data = await response.json();
        setCategories(data);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setError('Failed to load categories');
      }
    };
    fetchCategories();
  }, []);

  // Fetch subcategories when category changes
  useEffect(() => {
    const fetchSubcategories = async () => {
      if (selectedCategory !== 'all') {
        try {
          const categoryObj = categories.find(c => c.name.toLowerCase() === selectedCategory);
          if (categoryObj) {
            const response = await fetch(getApiUrl(`/api/admin/subcategories?category=${categoryObj._id}`));
            if (!response.ok) throw new Error('Failed to fetch subcategories');
            const data = await response.json();
            setSubcategories(data);
          }
        } catch (err) {
          console.error('Error fetching subcategories:', err);
          setError('Failed to load subcategories');
        }
      } else {
        setSubcategories([]);
      }
    };
    fetchSubcategories();
  }, [selectedCategory, categories]);

  // Fetch products
  useEffect(() => {
    setLoading(true);
    // Add loading class to body to prevent scrolling
    document.body.classList.add('loading');
    
    fetch(getApiUrl('/api/admin/products?populate=category,subcategory'), { headers: { 'x-admin': 'true' } })
      .then(res => res.json())
      .then(data => {
        let productsList = Array.isArray(data.products) ? data.products : Array.isArray(data) ? data : [];
        setProducts(productsList);
        setLoading(false);
        // Remove loading class from body
        document.body.classList.remove('loading');
      })
      .catch(err => {
        setError('Failed to load products');
        setLoading(false);
        // Remove loading class from body on error too
        document.body.classList.remove('loading');
      });
    
    // Cleanup function to remove loading class when component unmounts
    return () => {
      document.body.classList.remove('loading');
    };
  }, []);

  const handleAddToCart = (product) => {
    addToCart(product, 1, defaultCartSize(product), 'Default');
    setShowAddedToCart(product._id);
    setTimeout(() => setShowAddedToCart(null), 2000);
  };

  const filteredProducts = React.useMemo(() => {
    let filtered = products;
    
    const selectedCategoryObject = categories.find(cat => cat.name.toLowerCase() === selectedCategory);
    const selectedSubcategoryObject = subcategories.find(sub => sub.name.toLowerCase() === selectedSubcategory);

    console.log('Selected Category (string):', selectedCategory);
    console.log('Selected Category Object:', selectedCategoryObject);
    console.log('Selected Tag:', tag);
    
    // Filter by tag if specified in URL
    if (tag) {
      filtered = filtered.filter(product => 
        Array.isArray(product.tags) && 
        product.tags.some(productTag => 
          productTag.toLowerCase().trim() === tag.toLowerCase().trim()
        )
      );
    }
    
    // If rental category, filter for isRental: true and rentalStatus: 'approved'
    if (selectedCategory === 'rental') {
      filtered = filtered.filter(product => product.isRental && product.rentalStatus === 'approved');
    } else if (selectedCategory !== 'all' && selectedCategoryObject) {
      filtered = filtered.filter(product => {
        console.log('Product category (from product.category):', product.category, 'Selected category ID:', selectedCategoryObject._id); // Debug log
        return product.category === selectedCategoryObject._id;
      });
    }

    // Filter by subcategory if selected
    if (selectedSubcategory && selectedSubcategoryObject) {
      filtered = filtered.filter(product => {
        console.log('Product subcategory:', product.subcategory, 'Selected subcategory ID:', selectedSubcategoryObject._id); // Debug log
        return product.subcategory === selectedSubcategoryObject._id;
      });
    }

    // Filter by price range
    filtered = filtered.filter(product => product.price >= priceRange[0] && product.price <= priceRange[1]);

    // Sort products
    const sorted = [...filtered]; // Create a shallow copy before sorting
    switch (sortBy) {
      case 'price-low':
        sorted.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        sorted.sort((a, b) => b.price - a.price);
        break;
      case 'newest':
        sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); // Assuming createdAt for newest
        break;
      default:
        // featured - no sorting needed
        break;
    }

    return sorted;
  }, [products, selectedCategory, selectedSubcategory, sortBy, categories, subcategories, priceRange]);

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-3 sm:py-4 md:py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 md:mb-8 gap-3 sm:gap-4">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
          {tag ? `${tag.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Products` :
           selectedCategory === 'all' ? 'All Products' : 
           categories.find(c => c.name.toLowerCase() === selectedCategory)?.name || 'Products'}
        </h1>
        <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4 w-full sm:w-auto">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="md:hidden flex items-center space-x-2 text-gray-700 hover:text-pink-600 bg-white px-3 sm:px-4 py-2 rounded-lg border border-gray-300 shadow-sm"
          >
            <FunnelIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            <span className="text-xs sm:text-sm font-medium">Filters</span>
          </button>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="border border-gray-300 rounded-lg px-2 sm:px-3 py-2 pr-8 sm:pr-10 focus:outline-none focus:ring-2 focus:ring-pink-500 text-xs sm:text-sm md:text-base"
          >
            <option value="featured">Featured</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
            <option value="newest">Newest</option>
          </select>
        </div>
      </div>

      {/* Mobile Filter Overlay */}
      {showFilters && (
        <div className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-50">
          <div className="absolute right-0 top-0 h-full w-72 sm:w-80 bg-white shadow-xl overflow-y-auto">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Filters</h2>
                <button
                  onClick={() => setShowFilters(false)}
                  className="text-gray-500 hover:text-gray-700 p-2"
                >
                  <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>
              
              {/* Categories with Subcategories */}
              <div className="space-y-4 sm:space-y-6">
                <div>
                  <h3 className="font-medium text-gray-900 mb-3 sm:mb-4">Categories</h3>
                  <div className="space-y-2 sm:space-y-3">
                    <label className="flex items-center cursor-pointer p-2 rounded-lg hover:bg-gray-50">
                      <input
                        type="radio"
                        name="category"
                        value="all"
                        checked={selectedCategory === 'all'}
                        onChange={() => {
                          setSelectedCategory('all');
                          setSelectedSubcategory('');
                        }}
                        className="h-4 w-4 sm:h-5 sm:w-5 rounded-full border-2 border-pink-400 focus:ring-2 focus:ring-pink-500 transition mr-2 sm:mr-3"
                      />
                      <span className="text-sm sm:text-base text-gray-700 font-medium">All Products</span>
                    </label>
                    {categories.map((category) => (
                      <div key={category._id}>
                        <label className="flex items-center cursor-pointer p-2 rounded-lg hover:bg-gray-50">
                          <input
                            type="radio"
                            name="category"
                            value={category.name.toLowerCase()}
                            checked={selectedCategory === category.name.toLowerCase()}
                            onChange={() => {
                              setSelectedCategory(category.name.toLowerCase());
                              setSelectedSubcategory('');
                            }}
                            className="h-4 w-4 sm:h-5 sm:w-5 rounded-full border-2 border-pink-400 focus:ring-2 focus:ring-pink-500 transition mr-2 sm:mr-3"
                          />
                          <span className="text-sm sm:text-base text-gray-700 font-medium">{category.name}</span>
                        </label>
                        {/* Subcategories - Only visible when parent category is selected */}
                        {selectedCategory === category.name.toLowerCase() && subcategories.length > 0 && (
                          <div className="ml-6 sm:ml-8 mt-2 space-y-1 sm:space-y-2">
                            {subcategories.map((sub) => (
                              <label key={sub._id} className="flex items-center cursor-pointer p-2 rounded-lg hover:bg-gray-50">
                                <input
                                  type="radio"
                                  name="subcategory"
                                  value={sub.name.toLowerCase()}
                                  checked={selectedSubcategory === sub.name.toLowerCase()}
                                  onChange={(e) => {
                                    setSelectedSubcategory(e.target.value);
                                  }}
                                  className="h-4 w-4 rounded-full border-2 border-pink-400 focus:ring-2 focus:ring-pink-500 transition mr-3"
                                />
                                <span className="text-sm text-gray-600">{sub.name}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Price Range */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-4">Price Range</h3>
                  <div className="space-y-4">
                    <input
                      type="range"
                      min="0"
                      max="5000"
                      value={priceRange[1]}
                      onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>₹{priceRange[0]}</span>
                      <span>₹{priceRange[1]}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
        {/* Desktop Filters Sidebar */}
        <div className="hidden lg:block w-64 space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
            </div>

            {/* Categories with Subcategories */}
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">Categories</h3>
              <div className="space-y-2">
                <label className="flex items-center cursor-pointer p-2 rounded-lg hover:bg-gray-50">
                  <input
                    type="radio"
                    name="category"
                    value="all"
                    checked={selectedCategory === 'all'}
                    onChange={() => {
                      setSelectedCategory('all');
                      setSelectedSubcategory('');
                    }}
                    className="h-5 w-5 rounded-full border-2 border-pink-400 focus:ring-2 focus:ring-pink-500 transition mr-3"
                  />
                  <span className="text-gray-700 font-medium">All Products</span>
                </label>
                {categories.map((category) => (
                  <div key={category._id}>
                    <label className="flex items-center cursor-pointer p-2 rounded-lg hover:bg-gray-50">
                      <input
                        type="radio"
                        name="category"
                        value={category.name.toLowerCase()}
                        checked={selectedCategory === category.name.toLowerCase()}
                        onChange={() => {
                          setSelectedCategory(category.name.toLowerCase());
                          setSelectedSubcategory('');
                        }}
                        className="h-5 w-5 rounded-full border-2 border-pink-400 focus:ring-2 focus:ring-pink-500 transition mr-3"
                      />
                      <span className="text-gray-700 font-medium">{category.name}</span>
                    </label>
                    {/* Subcategories - Only visible when parent category is selected */}
                    {selectedCategory === category.name.toLowerCase() && subcategories.length > 0 && (
                      <div className="ml-8 mt-2 space-y-1">
                        {subcategories.map((sub) => (
                          <label key={sub._id} className="flex items-center cursor-pointer p-2 rounded-lg hover:bg-gray-50">
                            <input
                              type="radio"
                              name="subcategory"
                              value={sub.name.toLowerCase()}
                              checked={selectedSubcategory === sub.name.toLowerCase()}
                              onChange={(e) => {
                                setSelectedSubcategory(e.target.value);
                              }}
                              className="h-4 w-4 rounded-full border-2 border-pink-400 focus:ring-2 focus:ring-pink-500 transition mr-3"
                            />
                            <span className="text-sm text-gray-600">{sub.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Price Range */}
            <div className="mt-6">
              <h3 className="font-medium text-gray-900 mb-4">Price Range</h3>
              <div className="space-y-4">
                <input
                  type="range"
                  min="0"
                  max="5000"
                  value={priceRange[1]}
                  onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-sm text-gray-600">
                  <span>₹{priceRange[0]}</span>
                  <span>₹{priceRange[1]}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="flex-1">
          {loading ? (
            <div className="fixed inset-0 bg-white/80 flex items-center justify-center z-50 overflow-hidden">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-pink-600"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12 text-red-600">{error}</div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No products found</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
              {filteredProducts.map((product) => (
                <ProductCard
                  key={product._id}
                  product={product}
                  onAddToCart={() => handleAddToCart(product)}
                  isWishlisted={isWishlisted}
                  toggleWishlist={toggleWishlist}
                  showAddedToCart={showAddedToCart === product._id}
                        />
                      ))}
                    </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Products; 